import { BlockBase, PageType } from '../queries/page';

type Rates = Record<string, string>;

// Skip certain fields that shouldn't contain shortcodes
const skipFields = new Set([
    'id',
    'index',
    'hash',
    'blockName',
    'tag',
    'width',
    'height',
    'level',
    'size',
    'maxWidth',
    'maxWidthUnit',
    'templateID',
    'url',
    'focus',
    'service',
    '__isimage',
    '__meta',
]);

// Simple shortcode processor - processes [shortcode_name] format
function processShortcode(content: string, rates: Rates): string {
    if (typeof content !== 'string') {
        return content;
    }

    return content.replace(/\[([^\]]+)\]/g, (match, shortcodeName) => {
        const cleanName = shortcodeName.trim();

        return rates[cleanName] ?? match;
    });
}

function processValue(value: unknown, rates: Rates): unknown {
    // this means we are at a leaf and after checking if it
    // is a shortcode we process it.
    if (typeof value === 'string') {
        return /\[([^\]]+)\]/g.test(value) ? processShortcode(value, rates) : value;
    }

    // if we have a list, recurse into each element
    if (Array.isArray(value)) {
        return value.map(v => processValue(v, rates));
    }

    // we have an object, so send to processBlock to process its keys.
    if (typeof value === 'object' && value !== null) {
        return processBlock(value as BlockBase, rates);
    }

    return value;
}

function processBlock(block: BlockBase, rates: Rates): BlockBase {
    const result: Record<string, unknown> = {};

    // process value if it is not one of the skipped fields.
    for (const [key, value] of Object.entries(block)) {
        result[key] = skipFields.has(key) ? value : processValue(value, rates);
    }

    return result as BlockBase;
}

function processShortcodesInBlocks(blocks: BlockBase[], rates: Rates): BlockBase[] {
    return blocks.map(block => processBlock(block, rates));
}

async function processPageDataWithInterestShortcodes(pageData: PageType, interestRates?: Rates | null): Promise<PageType> {
    if (!interestRates || typeof interestRates !== 'object') {
        console.warn('Skipping interest rate shortcode processing due to invalid rates data.');
        return pageData;
    }

    // Only process if there's a blocks array to work with
    if (pageData && typeof pageData === 'object' && 'blocks' in pageData && Array.isArray((pageData as { blocks: unknown }).blocks)) {
        return {
            ...pageData,
            blocks: processShortcodesInBlocks(pageData.blocks, interestRates),
        };
    }

    return pageData;
}

export { processPageDataWithInterestShortcodes, processShortcodesInBlocks, processShortcode, type Rates };
